import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import qs from 'qs';

export interface ITypedQueryParamsOptions<T, R> {
  transform?: (parsed: T) => R;
}

/**
 * Generic hook for parsing URL query parameters with TypeScript support
 * 
 * @param options Configuration object with optional transform function
 * @returns Parsed and optionally transformed query parameters
 * 
 * @example
 * // Basic usage with type assertion
 * const params = useTypedQueryParams<{ id: string; page?: string }>();
 * 
 * @example 
 * // With transform function for computed values
 * const params = useTypedQueryParams<RawParams, ProcessedParams>({
 *   transform: (raw) => ({
 *     ...raw,
 *     computedValue: someCalculation(raw.input)
 *   })
 * });
 */
export function useTypedQueryParams<T = Record<string, any>, R = T>(
  options: ITypedQueryParamsOptions<T, R> = {}
): R {
  const location = useLocation();
  const [params, setParams] = useState<R>(() => {
    return parseQueryParams<T, R>(location.search, options);
  });

  useEffect(() => {
    const newParams = parseQueryParams<T, R>(location.search, options);
    setParams(newParams);
  }, [location.search, options.transform]);

  return params;
}

/**
 * Parse query parameters from a search string
 * Separated for testability and reuse in non-hook contexts
 */
export function parseQueryParams<T = Record<string, any>, R = T>(
  search: string,
  options: ITypedQueryParamsOptions<T, R> = {}
): R {
  const { transform } = options;
  
  const cleanQueryString = search.replace(/^\?/, '');
  
  if (!cleanQueryString) {
    return (transform ? transform({} as T) : {}) as R;
  }

  try {
    const parsed = qs.parse(cleanQueryString) as T;
    return transform ? transform(parsed) : (parsed as unknown as R);
  } catch (error) {
    console.error('Failed to parse query parameters:', error);
    return (transform ? transform({} as T) : {}) as R;
  }
}